// Geluid op het perceel uit de RIVM-geluidskaarten (10 m), per bron als
// {min, mean, max} in dB over het hele perceel (ca_reference, `rast`).

export type NoiseStats = { min: number; mean: number; max: number };

type Source = {
  key: string;
  label: string;
  metric: "Lden" | "Lnight";
  // WHO Environmental Noise Guidelines (2018); industrie heeft er geen.
  who?: number;
};

export const NOISE_SOURCES: Source[] = [
  { key: "GeluidLdenTotaal", label: "Alle bronnen", metric: "Lden" },
  { key: "GeluidLdenWeg", label: "Wegverkeer", metric: "Lden", who: 53 },
  { key: "GeluidLnightWeg", label: "Wegverkeer 's nachts", metric: "Lnight", who: 45 },
  { key: "GeluidLdenTrein", label: "Treinverkeer", metric: "Lden", who: 54 },
  { key: "GeluidLnightTrein", label: "Treinverkeer 's nachts", metric: "Lnight", who: 44 },
  { key: "GeluidLdenVlieg", label: "Vliegverkeer", metric: "Lden", who: 45 },
  { key: "GeluidLdenIndustrie", label: "Industrie", metric: "Lden" },
  { key: "GeluidLdenWind", label: "Windturbines", metric: "Lden", who: 45 },
];

// Klassen van 5 dB zoals de legenda van de Atlas Leefomgeving; de woorden
// zijn van ons. `low` is de ondergrens in dB Lden.
export const NOISE_LEGEND = [
  { low: -Infinity, name: "Rustig", color: "#3C5941" },
  { low: 45, name: "Weinig geluid", color: "#819470" },
  { low: 50, name: "Matig", color: "#D0D2A6" },
  { low: 55, name: "Veel geluid", color: "#FBF2C4" },
  { low: 60, name: "Zeer veel geluid", color: "#E0B881" },
  { low: 65, name: "Ernstig", color: "#D7995E" },
  { low: 70, name: "Extreem", color: "#B5543A" },
];

// Onder deze waarde is een bron niet van betekenis: de kaarten zijn modellen
// die ver van elke bron doorlopen tot 0 dB (industrie op de Veluwe).
const RELEVANT_FROM = { Lden: 40, Lnight: 30 };
// Lnight ligt voor dezelfde bron typisch ~10 dB onder Lden; de klasse van een
// nachtwaarde volgt daarom de Lden-klassen 10 dB hoger.
const NIGHT_OFFSET = 10;

export const noiseClass = (db: number, metric: "Lden" | "Lnight" = "Lden") => {
  const value = metric === "Lnight" ? db + NIGHT_OFFSET : db;
  return [...NOISE_LEGEND].reverse().find((c) => value >= c.low) ?? NOISE_LEGEND[0];
};

/** "56 dB" als het hele perceel gelijk is, anders "48–63 dB". */
export const formatRange = ({ min, max }: NoiseStats) =>
  min === max ? `${min} dB` : `${min}–${max} dB`;

export type NoiseRow = Source & {
  stats: NoiseStats;
  relevant: boolean;
  text: string;
  elem: { name: string; color: string };
  aboveWho: boolean;
};

const isStats = (v: unknown): v is NoiseStats =>
  !!v && typeof v === "object" && "max" in v && "min" in v && "mean" in v;

/** Alleen de bronnen die in de cube staan, in de vaste volgorde. */
export const noiseRows = (rast: Record<string, unknown> | undefined): NoiseRow[] =>
  NOISE_SOURCES.flatMap((source) => {
    const stats = rast?.[source.key];
    if (!isStats(stats)) return [];
    const relevant = stats.max >= RELEVANT_FROM[source.metric];
    if (!relevant) return [];
    return [
      {
        ...source,
        stats,
        relevant,
        text: relevant ? `${formatRange(stats)}` : "geen relevante bijdrage",
        // De ongunstigste plek op het perceel bepaalt de klasse
        elem: noiseClass(stats.max, source.metric),
        aboveWho: relevant && source.who !== undefined && stats.max > source.who,
      },
    ];
  });
